import { Controller } from "@hotwired/stimulus"

// Searchable scope picker for `/insights`. 3-tab picker (Ad Account /
// Business / Page) — the old "Entire Account" tab was removed
// 2026-05-22 because aggregator-level insights are moving to a
// separate generator class. Server renders every option for every
// tab; tabs switch which pane is visible.
// Search filters within the active tab only — switching tabs clears the
// query and re-shows the new pane's options.
//
// Keyboard:
//   - Click button → toggle dropdown
//   - Arrow Down/Up while focus is in the search input → move highlight
//     within the visible pane
//   - Enter → activate highlighted option
//   - Escape → close
//
// Outside-click closes the menu.
export default class extends Controller {
  static targets = ["button", "menu", "search", "option", "tab", "pane", "empty", "label"]
  static values = {
    activeTab: { type: String, default: "ad_account" },
  }

  connect() {
    console.log(
      `[insights-scope-selector] connect: ${this.optionTargets.length} total options, activeTab=${this.activeTabValue}`,
    )
    this.highlightIndex = -1
    this._closeOnOutside = this._closeOnOutside.bind(this)
    this._renderTabs()
  }

  disconnect() {
    document.removeEventListener("click", this._closeOnOutside)
  }

  toggle(event) {
    event?.stopPropagation()
    const isHidden = this.menuTarget.classList.contains("hidden")
    if (isHidden) this.open()
    else this.close()
  }

  open() {
    this.menuTarget.classList.remove("hidden")
    document.addEventListener("click", this._closeOnOutside)
    setTimeout(() => {
      if (this.hasSearchTarget) this.searchTarget.focus()
    }, 10)
  }

  close() {
    this.menuTarget.classList.add("hidden")
    document.removeEventListener("click", this._closeOnOutside)
    this.highlightIndex = -1
    this._refreshHighlight()
  }

  chooseTab(event) {
    const tab = event.currentTarget.dataset.tab
    if (!tab || tab === this.activeTabValue) return
    console.log(`[insights-scope-selector] chooseTab: ${tab}`)
    this.activeTabValue = tab
    if (this.hasSearchTarget) this.searchTarget.value = ""
    this._renderTabs()
    this.highlightIndex = -1
    this._refreshHighlight()
    if (this.hasEmptyTarget) this.emptyTarget.classList.add("hidden")
    if (this.hasSearchTarget) this.searchTarget.focus()
  }

  filter(event) {
    const q = (event.target.value || "").trim().toLowerCase()
    const activeOptions = this._activeOptions()
    let firstVisible = -1
    activeOptions.forEach((opt) => {
      const blob = (opt.dataset.search || "").toLowerCase()
      const visible = q === "" || blob.includes(q)
      opt.classList.toggle("hidden", !visible)
      if (visible && firstVisible === -1) {
        firstVisible = this.optionTargets.indexOf(opt)
      }
    })
    this.highlightIndex = firstVisible
    this._refreshHighlight()
    if (this.hasEmptyTarget) {
      const anyVisible = activeOptions.some((o) => !o.classList.contains("hidden"))
      this.emptyTarget.classList.toggle("hidden", anyVisible)
    }
  }

  keydown(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      this.close()
      this.buttonTarget?.focus()
      return
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault()
      const visible = this._visibleActiveOptions()
      if (visible.length === 0) return
      const cur = visible.indexOf(this.optionTargets[this.highlightIndex])
      const next = event.key === "ArrowDown"
        ? (cur + 1) % visible.length
        : (cur - 1 + visible.length) % visible.length
      this.highlightIndex = this.optionTargets.indexOf(visible[next])
      this._refreshHighlight()
      visible[next].scrollIntoView({ block: "nearest" })
      return
    }
    if (event.key === "Tab") {
      const tabs = this.tabTargets
      const order = ["ad_account", "business", "page"]
      const cur = order.indexOf(this.activeTabValue)
      if (cur >= 0 && tabs.length > 0) {
        event.preventDefault()
        const nextIdx = event.shiftKey
          ? (cur - 1 + order.length) % order.length
          : (cur + 1) % order.length
        const target = tabs.find((t) => t.dataset.tab === order[nextIdx])
        if (target) target.click()
      }
      return
    }
    if (event.key === "Enter") {
      event.preventDefault()
      if (this.highlightIndex >= 0) {
        this.optionTargets[this.highlightIndex].click()
      }
    }
  }

  selectFromClick(event) {
    const url = event.currentTarget.dataset.url
    if (url) {
      this.close()
      window.Turbo ? window.Turbo.visit(url) : (window.location.href = url)
    }
  }

  // private

  _activeOptions() {
    return this.optionTargets.filter((o) => o.dataset.tab === this.activeTabValue)
  }

  _visibleActiveOptions() {
    return this._activeOptions().filter((o) => !o.classList.contains("hidden"))
  }

  _renderTabs() {
    this.tabTargets.forEach((t) => {
      t.toggleAttribute("data-active", t.dataset.tab === this.activeTabValue)
    })
    this.paneTargets.forEach((p) => {
      p.classList.toggle("hidden", p.dataset.pane !== this.activeTabValue)
    })
  }

  _refreshHighlight() {
    this.optionTargets.forEach((opt, i) => {
      opt.toggleAttribute("data-highlighted", i === this.highlightIndex)
    })
  }

  _closeOnOutside(event) {
    if (this.element.contains(event.target)) return
    this.close()
  }
}
